import { isPlatformServer } from '@angular/common';
import { Component, PLATFORM_ID, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title, Meta } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { HomePageData } from '../../../shared/models/marketing.models';
import { PagesService } from '../../../shared/services/pages.service';
import { buildPageTitle, SITE_DESCRIPTION } from '../../../shared/constants/site-meta';
import { MarketingWhyChooseComponent } from '../../components/marketing-why-choose.component';
import { MarketingSuccessRoadmapComponent } from '../../components/marketing-success-roadmap.component';
import { MarketingGlobalSuccessStoriesComponent } from '../../components/marketing-global-success-stories.component';
import { MarketingTestimonialsComponent } from '../../components/marketing-testimonials.component';
import { resolveMilestoneCtaHref } from '../../utils/milestone-cta';
import { LeadCaptureFormComponent } from './lead-capture-form.component';

interface FaqItem {
  question: string;
  answer: string;
}

interface ServiceItem {
  icon: string;
  title: string;
  description: string;
}

type LeadFormLoadState =
  | { kind: 'pending' }
  | { kind: 'fallback' }
  | { kind: 'ok'; home: HomePageData };

const FALLBACK_STATS = [
  { value: '12K+', label: 'Students Counseled' },
  { value: '99%', label: 'Visa Success' },
  { value: '500+', label: 'Partner Universities' },
  { value: '15+', label: 'Study Destinations' },
];

const FALLBACK_HERO_FEATURES = [
  'Free 45-minute counseling with senior experts',
  '500+ partner universities across 15+ countries',
  '99% visa success rate with end-to-end support',
  'Scholarship & education loan assistance included',
];

@Component({
  selector: 'app-lead-form-page',
  standalone: true,
  imports: [
    RouterLink,
    LeadCaptureFormComponent,
    MarketingWhyChooseComponent,
    MarketingSuccessRoadmapComponent,
    MarketingGlobalSuccessStoriesComponent,
    MarketingTestimonialsComponent,
  ],
  templateUrl: './lead-form-page.component.html',
})
export class LeadFormPageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  readonly openFaq = signal<number | null>(0);

  readonly homeState = toSignal(
    this.pages.getMarketingHome().pipe(
      map((home) => ({ kind: 'ok' as const, home })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'fallback' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies LeadFormLoadState },
  );

  readonly services: ServiceItem[] = [
    {
      icon: '🎓',
      title: 'University Admissions',
      description: 'Profile evaluation, shortlisting, and application support for top global universities.',
    },
    {
      icon: '✈️',
      title: 'Visa Assistance',
      description: 'Document preparation, SOP review, and mock interviews for a smooth visa approval.',
    },
    {
      icon: '💰',
      title: 'Education Loans',
      description: 'Partner bank tie-ups and guidance to secure affordable education financing.',
    },
    {
      icon: '🏆',
      title: 'Scholarships',
      description: 'Discover and apply for merit-based and need-based scholarships worldwide.',
    },
    {
      icon: '📝',
      title: 'IELTS / PTE Guidance',
      description: 'Test prep strategies, score improvement plans, and booking assistance.',
    },
    {
      icon: '📄',
      title: 'SOP / LOR Support',
      description: 'Expert-crafted statements and recommendation letters that stand out.',
    },
    {
      icon: '🏠',
      title: 'Accommodation Assistance',
      description: 'Help finding safe, affordable housing near your university campus.',
    },
    {
      icon: '🌐',
      title: 'Pre-Departure Briefing',
      description: 'Orientation on culture, banking, insurance, and settling abroad.',
    },
  ];

  readonly faqs: FaqItem[] = [
    {
      question: 'Is the counseling session really free?',
      answer:
        'Yes, absolutely. Your first 45-minute counseling session is completely free with no hidden charges or obligation to enroll.',
    },
    {
      question: 'Which countries do you support?',
      answer:
        'We support 15+ destinations including the UK, Canada, Germany, Australia, USA, Ireland, France, New Zealand, and Singapore.',
    },
    {
      question: 'How soon will a counselor contact me?',
      answer:
        'Our team typically reaches out within 24 hours on business days. Urgent queries are prioritized within 4 hours.',
    },
    {
      question: 'Do you help with education loans?',
      answer:
        'Yes. We partner with leading banks and NBFCs to help you secure education loans at competitive interest rates.',
    },
    {
      question: 'What is your visa success rate?',
      answer:
        'We maintain a 99% visa success rate through meticulous document preparation, SOP crafting, and mock interview training.',
    },
    {
      question: 'Can I apply if I haven\'t taken IELTS yet?',
      answer:
        'Absolutely. Many universities accept applications without test scores initially. We\'ll guide you on the best timeline for your intake.',
    },
  ];

  constructor() {
    effect(() => {
      const s = this.homeState();
      if (s.kind === 'ok' && s.home.seo?.title) {
        this.title.setTitle(
          buildPageTitle('Start Your Global Education Journey | Free Study Abroad Counseling'),
        );
      }
    });
    this.title.setTitle(
      buildPageTitle('Start Your Global Education Journey | Free Study Abroad Counseling'),
    );
    this.meta.updateTag({ name: 'description', content: SITE_DESCRIPTION });
    this.meta.updateTag({ property: 'og:title', content: 'Start Your Global Education Journey Today' });
    this.meta.updateTag({
      property: 'og:description',
      content:
        'Get free expert study abroad counseling. University admissions, visa support, scholarships & loans — trusted by 12,000+ students.',
    });
  }

  stats(home?: HomePageData) {
    const fromCms = home?.stats?.filter((s) => s.value?.trim() && s.label?.trim());
    return fromCms?.length ? fromCms : FALLBACK_STATS;
  }

  heroFeatures(home?: HomePageData) {
    const fromCms = home?.hero?.features?.filter(Boolean);
    return fromCms?.length ? fromCms : FALLBACK_HERO_FEATURES;
  }

  heroBadge(home?: HomePageData) {
    return home?.hero?.badge?.trim() || 'Trusted by 12,000+ students globally';
  }

  announcement(home?: HomePageData) {
    return home?.announcement;
  }

  destinations(home?: HomePageData) {
    return home?.destinations?.countries ?? [];
  }

  destinationsTitle(home?: HomePageData) {
    return home?.destinations?.title?.trim() || 'Top Study Destinations';
  }

  destinationsSubtitle(home?: HomePageData) {
    return (
      home?.destinations?.subtitle?.trim() ||
      'Handpicked countries with strong post-study work options, scholarships, and English-taught programs.'
    );
  }

  successStoryItems(home: HomePageData) {
    return (home.successStories?.items ?? []).map((story) => ({
      ...story,
      ctaHref: resolveMilestoneCtaHref(story.ctaHref, story.title),
    }));
  }

  testimonialItems(home?: HomePageData) {
    return home?.testimonials?.items ?? [];
  }

  successStoriesTitle(home?: HomePageData) {
    const fromCms = home?.successStories?.title?.trim();
    if (fromCms && !this.isCmsPlaceholder(fromCms)) return fromCms;
    return 'Global Success Stories';
  }

  successStoriesSubtitle(home?: HomePageData) {
    const fromCms = home?.successStories?.subtitle?.trim();
    if (fromCms && !this.isCmsPlaceholder(fromCms)) return fromCms;
    return 'Real journeys, life-changing achievements, and global impact.';
  }

  partners(home?: HomePageData) {
    return home?.partners?.items?.filter((p) => p.name?.trim()) ?? [];
  }

  partnersTitle(home?: HomePageData) {
    return home?.partners?.title?.trim() || 'University Partners';
  }

  destinationBackground(country: HomePageData['destinations']['countries'][number]): string {
    const url = country.image?.cdnUrl?.trim();
    if (url) {
      return `linear-gradient(180deg, rgba(10, 23, 51, 0.12) 0%, rgba(10, 23, 51, 0.35) 100%), url(${url})`;
    }
    return 'linear-gradient(140deg, #1d3b5c 0%, #3c6890 100%)';
  }

  destinationMeta(country: HomePageData['destinations']['countries'][number]): string {
    const raw = country.shortDescription?.trim();
    if (!raw) return 'Explore programs';
    const firstSentence = raw.split(/(?<=[.!?])\s+/)[0]?.trim() ?? raw;
    if (firstSentence.length <= 56) return firstSentence;
    return `${firstSentence.slice(0, 53).trimEnd()}…`;
  }

  toggleFaq(index: number): void {
    this.openFaq.update((current) => (current === index ? null : index));
  }

  scrollToForm(): void {
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private isCmsPlaceholder(value: string): boolean {
    const v = value.trim().toLowerCase();
    return /^test\d*$/.test(v) || v.startsWith('test');
  }
}
